import { ScoredStock, StockFactors, PillarScores } from './compute';
import { DEFAULT_PILLAR_WEIGHTS, QUALITY_PILLAR_KEYS } from './factor-config';

// ─── Factor Definitions ─────────────────────────────────────────────────────

// Mirrors compute.ts PILLAR_FACTORS.nonFinancial. All weights = 1 — forward/trailing
// skew is achieved by factor *count* per pillar, not by per-factor weights.
export const QUALITY_FACTORS: Record<keyof PillarScores, { key: keyof StockFactors; invert: boolean; weight?: number }[]> = {
  profitability: [
    // Forward (3)
    { key: 'fwdRoe', invert: false },
    { key: 'fwdRoa', invert: false },
    { key: 'fwdNetMargin', invert: false },
    // Trailing (3)
    { key: 'roic', invert: false },
    { key: 'grossProfitToAssets', invert: false },
    { key: 'accrualsRatio', invert: true },
  ],
  growth: [
    // Forward (4)
    { key: 'fwdEpsCagr2yr', invert: false },
    { key: 'fwdRevCagr2yr', invert: false },
    { key: 'fwdEpsCagr3yr', invert: false },
    { key: 'nextQtrEpsGrowth', invert: false },
    // Derived forward — margin trajectory
    { key: 'fwdOperLeverage', invert: false },
    // Trailing (1) — cash-based reality check
    { key: 'fcfGrowth3yr', invert: false },
  ],
  safety: [
    // Forward (1)
    { key: 'fwdDebtToEbitda', invert: true },
    // Trailing (3)
    { key: 'currentRatio', invert: false },
    { key: 'debtToEbitda', invert: true },
    { key: 'beta', invert: true },
  ],
  shareholderReturns: [
    // Forward (2)
    { key: 'fwdDividendYield', invert: false },
    { key: 'impliedBuybackYield', invert: false },
    // Trailing (3)
    { key: 'buybackYield', invert: false },
    { key: 'netDebtPaydownYield', invert: false },
    { key: 'insiderNetBuy', invert: false },
  ],
  momentum: [
    // Z-axis "Momentum" composite. Display-only — does NOT influence Alpha Zone selection.
    // `nearHigh` was dropped after IC audit found t-stat 0.75, L/S spread −12.2% over 9yr cache.
    { key: 'momentum', invert: false },             // 12-1 price momentum (Jegadeesh-Titman)
    { key: 'shortTermReversal', invert: false },    // 1-mo return — high = positive momentum (no mean-reversion inversion)
    { key: 'epsRevision', invert: false },          // analyst upgrades/downgrades
    { key: 'earningsSurprise', invert: false },     // post-earnings drift
  ],
};

export const VALUATION_FACTORS: { key: keyof StockFactors; invert: boolean; weight?: number }[] = [
  // All weights = 1; forward/trailing skew achieved by factor *count*, not weighting.
  // Non-fin: 6 forward + 3 trailing = 66.7% forward (2/3 target).
  // Forward (6)
  // All weights = 1. Long-horizon emphasis comes from HAVING three forward P/Es (1yr/2yr/3yr) rather than from weighting.
  { key: 'fwdPE', invert: true },
  { key: 'fwdPE2yr', invert: true },
  { key: 'fwdPE3yr', invert: true }, // BBG BEST_EPS_3BF
  { key: 'fwdEvToEbitda', invert: true },
  { key: 'pegRatio', invert: true },
  { key: 'evToEbitda', invert: true },
  // Dropped 2026-04-23 evening: fwdPtoSales + fwdEvToRevenue (sales-based across mixed business types),
  // evToGrossProfit (backward), pToFcf (penalizes reinvestment-heavy compounders).
  // Bank/insurance-specific — these render when the stock is in the financial peer group
  // (banks are legitimately priced on book; non-fins have these nulled).
  { key: 'priceToBook', invert: true, weight: 1 },
  { key: 'fwdPtoBook', invert: true, weight: 1 },
  // Bank-specific
  { key: 'pToRevenue', invert: true },
  // REIT-specific — these render when the stock is in the REIT peer group.
  // Added 2026-05-23 to bring client-side list to the canonical 11 peer-z
  // factors (matches the 14 V count in data/model-stats.json: 11 peer + 3
  // absolute). Without these the panel was missing two factors silently.
  { key: 'pToFfo' as keyof StockFactors, invert: true, weight: 1 },
  { key: 'pToNav' as keyof StockFactors, invert: true, weight: 1 },
];

// All quality factor keys (flat list)
export const ALL_QUALITY_KEYS: (keyof StockFactors)[] =
  Object.values(QUALITY_FACTORS).flat().map(f => f.key);

export const ALL_VALUATION_KEYS: (keyof StockFactors)[] =
  VALUATION_FACTORS.map(f => f.key);

// ─── Scoring Helpers (mirrors compute.ts) ────────────────────────────────────

function computeStats(values: (number | null)[]): { mean: number; std: number } | null {
  const valid = values.filter((v): v is number => v !== null && isFinite(v));
  if (valid.length < 5) return null;
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const std = Math.sqrt(valid.reduce((a, b) => a + (b - mean) ** 2, 0) / valid.length);
  return { mean, std: std || 1 };
}

function zscoreVal(val: number | null, stats: { mean: number; std: number } | null): number | null {
  if (val === null || !stats) return null;
  return (val - stats.mean) / stats.std;
}

function clamp(v: number | null, lo = -3, hi = 3): number | null {
  if (v === null) return null;
  return Math.min(Math.max(v, lo), hi);
}

function avgZ(zscores: (number | null)[]): number | null {
  const valid = zscores.filter((v): v is number => v !== null);
  if (valid.length === 0) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

// ─── Client-Side Rescoring ───────────────────────────────────────────────────

// Fast-path rescore used when only pillar weight sliders moved. Reuses the
// server's pillarScores (which were computed peer-group-aware: bank factors
// for banks, non-bank factors for everyone else), re-blends them with the
// user's slider weights, and re-z-scores quality across the universe.
// Valuation is left untouched since it's a separate axis with no slider.
function rescoreFromServerPillars(
  stocks: ScoredStock[],
  pillarWeights: Record<keyof PillarScores, number>,
): ScoredStock[] {
  const pillarKeys = QUALITY_PILLAR_KEYS;
  const weightedAvg = (s: ScoredStock): number | null => {
    let wSum = 0, vSum = 0;
    for (const pk of pillarKeys) {
      const w = pillarWeights[pk];
      const pv = s.pillarScores[pk];
      if (w > 0 && pv !== null && pv !== undefined) { vSum += pv * w; wSum += w; }
    }
    return wSum > 0 ? vSum / wSum : null;
  };
  const qualityRaw = stocks.map(weightedAvg);
  const qualityStats = computeStats(qualityRaw);
  return stocks.map((s, i) => ({
    ...s,
    qualityScore: clamp(zscoreVal(qualityRaw[i], qualityStats ?? null)),
  }));
}

export function rescoreStocks(
  stocks: ScoredStock[],
  enabledQuality: Set<keyof StockFactors>,
  enabledValuation: Set<keyof StockFactors>,
  pillarWeights?: Record<keyof PillarScores, number>,
): ScoredStock[] {
  // Fast path — only pillar weight sliders moved (all factors still enabled).
  // Reuse the server's peer-group-aware pillarScores instead of recomputing
  // from factors with the unified non-bank QUALITY_FACTORS list. This keeps
  // banks scored against bank peers (NIM/efficiency/equityToAssets/etc.)
  // when the user explores via sliders. The slow path below still runs when
  // the user toggles individual factor checkboxes.
  const allQualityEnabled = ALL_QUALITY_KEYS.every(k => enabledQuality.has(k));
  const allValuationEnabled = ALL_VALUATION_KEYS.every(k => enabledValuation.has(k));
  if (allQualityEnabled && allValuationEnabled && pillarWeights) {
    return rescoreFromServerPillars(stocks, pillarWeights);
  }

  // TODO(sub-factor-toggle-ui): the slow path below uses a single unified
  // QUALITY_FACTORS list and z-scores across the whole universe — it does NOT
  // mirror the server's peer-group split (PILLAR_FACTORS.financial vs
  // .nonFinancial in compute.ts). Today this path is unreachable because the
  // UI only exposes pillar weight sliders, which the fast path above handles.
  // Before shipping per-sub-factor checkbox toggles, add bank-aware factor
  // lists here (QUALITY_FACTORS_FINANCIAL / QUALITY_FACTORS_NONFINANCIAL),
  // route each stock via isBankLike(), and z-score within peer groups.
  // Otherwise toggling a factor will silently misscore banks against non-bank
  // peers — the same class of bug the fast path was added to prevent.

  // Step 1: Compute stats for enabled factors only
  const stats: Partial<Record<keyof StockFactors, { mean: number; std: number } | null>> = {};
  const allEnabled = new Set([...enabledQuality, ...enabledValuation]);
  for (const key of allEnabled) {
    stats[key] = computeStats(stocks.map(s => s.factors[key] as number | null));
  }

  // Step 2: Per-stock z-score computation
  const scored = stocks.map(s => {
    const f = s.factors;
    const z = (k: keyof StockFactors, invert: boolean) => {
      if (!allEnabled.has(k)) return null;
      const zv = clamp(zscoreVal(f[k], stats[k] ?? null));
      return zv !== null ? (invert ? -zv : zv) : null;
    };

    // Pillar scores from enabled quality factors
    const pillarRaw: Record<string, number | null> = {};
    for (const [pillar, factors] of Object.entries(QUALITY_FACTORS)) {
      const enabled = factors.filter(f => enabledQuality.has(f.key));
      pillarRaw[pillar] = enabled.length > 0 ? avgZ(enabled.map(f => z(f.key, f.invert))) : null;
    }

    // Valuation from enabled valuation factors
    const enabledVal = VALUATION_FACTORS.filter(f => enabledValuation.has(f.key));
    const rawValuation = enabledVal.length > 0 ? avgZ(enabledVal.map(f => z(f.key, f.invert))) : null;

    return { stock: s, pillarRaw, rawValuation };
  });

  // Step 3: Z-score pillar averages, then z-score quality across universe
  const pillarKeys = QUALITY_PILLAR_KEYS;
  const weights = pillarWeights ?? DEFAULT_PILLAR_WEIGHTS;
  const pillarStats: Record<string, { mean: number; std: number } | null> = {};
  for (const pk of pillarKeys) {
    pillarStats[pk] = computeStats(scored.map(s => s.pillarRaw[pk]));
  }
  const valStats = computeStats(scored.map(s => s.rawValuation));

  const weightedQualityAvg = (pz: (number | null)[]) => {
    let wSum = 0, vSum = 0;
    pillarKeys.forEach((pk, idx) => {
      const w = weights[pk];
      if (pz[idx] !== null && w > 0) { vSum += pz[idx]! * w; wSum += w; }
    });
    return wSum > 0 ? vSum / wSum : null;
  };

  const qualityRaw = scored.map(s => {
    const pz = pillarKeys.map(pk => clamp(zscoreVal(s.pillarRaw[pk], pillarStats[pk] ?? null)));
    return weightedQualityAvg(pz);
  });
  const qualityStats = computeStats(qualityRaw);

  return scored.map((s, _i) => {
    const pz = pillarKeys.map(pk => clamp(zscoreVal(s.pillarRaw[pk], pillarStats[pk] ?? null)));
    const qualityAvg = weightedQualityAvg(pz);
    const qualityScore = clamp(zscoreVal(qualityAvg, qualityStats ?? null));
    const valuationScore = clamp(zscoreVal(s.rawValuation, valStats ?? null));

    return {
      ...s.stock,
      pillarScores: {
        profitability: clamp(zscoreVal(s.pillarRaw.profitability, pillarStats.profitability ?? null)),
        growth: clamp(zscoreVal(s.pillarRaw.growth, pillarStats.growth ?? null)),
        safety: clamp(zscoreVal(s.pillarRaw.safety, pillarStats.safety ?? null)),
        shareholderReturns: clamp(zscoreVal(s.pillarRaw.shareholderReturns, pillarStats.shareholderReturns ?? null)),
        momentum: s.stock.pillarScores?.momentum ?? null, // pass through from server (client rescoring doesn't touch momentum pillar)
      },
      qualityScore,
      valuationScore,
    };
  });
}

// ─── Label Utility ───────────────────────────────────────────────────────────

export function factorLabel(key: string): string {
  // Convert camelCase to Title Case with special handling
  const special: Record<string, string> = {
    roe: 'ROE (BBG adj.)', roa: 'ROA (BBG adj.)', roic: 'ROIC (BBG adj.)',
    cfoToAssets: 'CFO / Assets',
    grossProfitToAssets: 'Gross Profit / Assets (BBG adj.)',
    fwdEpsGrowth: 'Fwd Adj. EPS Growth 1yr (BBG)',
    fwdRevGrowth: 'Rev / Share Growth 1yr (BBG)',
    fwdEpsCagr2yr: 'Fwd Adj. EPS CAGR 2yr (BBG)',
    fwdEpsCagr3yr: 'Fwd Adj. EPS CAGR 3yr (BBG)',
    fwdOperLeverage: 'Fwd Operating Leverage',
    fwdRevCagr2yr: 'Fwd Rev / Share CAGR 2yr (BBG)',
    epsRevision: 'EPS Revision Momentum (BBG)',
    epsCAGR3yr: 'Adj. EPS CAGR 3yr (BBG)',
    fcfGrowth3yr: 'FCF Growth 3yr',
    grossMarginExpansion3yr: 'Gross Margin Expansion 3yr',
    opMarginExpansion3yr: 'Op. Margin Expansion 3yr',
    altmanZ: 'Altman Z-Score',
    piotroski: 'Piotroski F-Score',
    debtToEbitda: 'Debt / EBITDA (BBG)',
    interestCoverage: 'Interest Coverage (BBG)',
    epsVariability: 'EPS Variability',
    beta: 'Beta',
    currentRatio: 'Current Ratio (BBG)',
    fcfToDebt: 'FCF / Debt',
    netDebtPaydownYield: 'Net Debt Paydown Yield',
    dividendYield: 'Dividend Yield',
    buybackYield: 'Net Buyback Yield (BBG Shares)',
    fwdPE: 'Fwd P/E 1yr (BBG)',
    fwdPE2yr: 'Fwd P/E 2yr (BBG)',
    fwdPE3yr: 'Fwd P/E 3yr (BBG)',
    fwdEvToRevenue: 'Fwd EV / Revenue (BBG)',
    fwdEvToEbitda: 'Fwd EV / EBITDA (BBG)',
    pegRatio: 'PEG Ratio (BBG)',
    evToEbitda: 'EV / EBITDA',
    pToFcf: 'Price / FCF',
    evToGrossProfit: 'EV / Gross Profit',
    grossMargin: 'Gross Margin (BBG adj.)',
    netMargin: 'Net Margin',
    accrualsRatio: 'Accruals Ratio',
    assetGrowth: 'Asset Growth',
    nim: 'Net Interest Margin',
    efficiencyRatio: 'Efficiency Ratio',
    provisionsToNII: 'Provisions / NII',
    revenueGrowth3yr: 'Revenue Growth 3yr',
    equityToAssets: 'Equity / Assets',
    retainedEarningsToAssets: 'Retained Earnings / Assets',
    priceToBook: 'Price / Book',
    pToRevenue: 'Price / Sales',
    pToFfo: 'Price / FFO (REIT)',
    pToNav: 'Price / NAV (REIT)',
    momentum: '12-1 Momentum',
    nearHigh: '52-Week High %',
    shortTermReversal: '1-Month Return',
    insiderNetBuy: 'Insider Net Buying (SEC)',
    earningsSurprise: 'Earnings Surprise (BBG)',
    nextQtrEpsGrowth: 'Next Qtr EPS Growth YoY (BBG)',
    fwdPtoBook: 'Fwd Price / Book (BBG)',
    fwdPtoSales: 'Fwd Price / Sales (BBG, buyback-adj.)',
    fwdRoe: 'Fwd ROE (BBG)',
    fwdRoa: 'Fwd ROA (BBG)',
    fwdDividendYield: 'Fwd Dividend Yield (BBG)',
    impliedBuybackYield: 'Fwd Buyback Yield (implied)',
  };
  return special[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
}

export function factorInfo(key: string): { formula: string; description: string } | null {
  const info: Record<string, { formula: string; description: string }> = {
    // Profitability
    roe: { formula: 'Net Income / Shareholders\' Equity', description: 'How efficiently the company generates profit from shareholder capital. Higher = better. Negative equity with positive income is treated as top-tier (strong capital returns).' },
    roa: { formula: 'Net Income / Total Assets', description: 'Profit generated per dollar of assets. Measures how efficiently management deploys the asset base.' },
    roic: { formula: 'EBIT × (1 - Tax Rate) / Invested Capital', description: 'After-tax operating return on debt + equity capital. The gold standard of profitability — measures true economic returns.' },
    grossProfitToAssets: { formula: 'Gross Profit / Total Assets', description: 'Novy-Marx profitability factor. Measures asset-light profitability before overhead. Strong predictor of future returns.' },
    grossMargin: { formula: 'Gross Profit / Revenue', description: 'Percentage of revenue retained after cost of goods. Indicates pricing power and competitive moat.' },
    netMargin: { formula: 'Net Income / Revenue', description: 'Bottom-line profitability — what percentage of each revenue dollar becomes profit after all expenses.' },
    cfoToAssets: { formula: 'Operating Cash Flow / Total Assets', description: 'Cash-based profitability. Unlike earnings, cash flow is hard to manipulate. Higher = more genuine profitability.' },
    accrualsRatio: { formula: '(Net Income - Operating Cash Flow) / Total Assets', description: 'Gap between reported earnings and cash flow. Lower (more negative) = higher earnings quality. Companies with large accruals tend to underperform.' },
    assetGrowth: { formula: 'YoY % Change in Total Assets', description: 'Conservative asset growers outperform aggressive ones (asset growth anomaly). Lower = better. Rapid asset expansion often destroys value.' },
    nim: { formula: 'Net Interest Income / Total Assets', description: 'Bank-specific: the spread earned between lending and deposit rates, scaled by assets. Core measure of bank profitability.' },
    efficiencyRatio: { formula: 'Operating Expenses / Revenue', description: 'Bank-specific: how much it costs to generate each dollar of revenue. Lower = more efficient operations.' },
    provisionsToNII: { formula: 'Loan Loss Provisions / Net Interest Income', description: 'Bank-specific earnings-quality factor. Replaces Accruals Ratio for financials (CFO is distorted by deposit/loan flows). Lower = higher-quality earnings (fewer credit losses per dollar of interest income). Inverted in scoring.' },
    // Growth
    fwdEpsGrowth: { formula: 'Bloomberg Fwd EPS / Bloomberg Cont. Ops EPS (TTM) - 1', description: 'Bloomberg consensus forward EPS vs Bloomberg continuing operations EPS (excludes discontinued ops and one-time items). Cleaner than GAAP diluted EPS as the growth base.' },
    fwdRevGrowth: { formula: '(Revenue Per Share Now / Revenue Per Share Prior Year) - 1', description: 'Bloomberg revenue per share growth — normalizes for share count changes. A company growing revenue 20% while issuing 15% more stock is only growing 5% per share. More accurate than raw revenue growth.' },
    fwdEpsCagr2yr: { formula: '(Year 2 Consensus EPS / Bloomberg Cont. Ops EPS)^(1/2) - 1', description: 'Annualized EPS growth rate from Bloomberg continuing ops EPS to year-2 analyst consensus. Captures medium-term earnings trajectory on a clean basis.' },
    fwdEpsCagr3yr: { formula: '(BBG BEST_EPS_3BF / Bloomberg Cont. Ops EPS)^(1/3) - 1', description: 'Annualized EPS growth to year-3 consensus. Uses Bloomberg BEST_EPS at 3BF horizon (added 2026-04-24). Rewards real 3-year compounders and symmetric with the Valuation side which already uses fwdPE3yr.' },
    fwdOperLeverage: { formula: 'fwdEpsCagr2yr − fwdRevCagr2yr (clamped ±30%)', description: 'Forward operating leverage — EPS growth minus revenue growth over the next 2 years. Positive = margins expanding (software-like compounders). Negative = margins compressing (revenue-only growers). Added 2026-04-24 to differentiate quality of growth.' },
    fwdRevCagr2yr: { formula: '(Year 2 Revenue / Current Revenue)^(1/2) - 1', description: 'Annualized revenue growth over the next 2 years per analyst consensus.' },
    epsRevision: { formula: '(Current BBG Consensus EPS - Prior Snapshot EPS) / |Prior EPS|', description: 'Earnings revision momentum — measures whether analysts are upgrading or downgrading consensus EPS estimates. Most predictive short-term signal in academic literature. Positive = upgrades, negative = downgrades.' },
    epsCAGR3yr: { formula: '(Bloomberg Cont. Ops EPS / EPS 3 Years Ago)^(1/3) - 1', description: 'Historical 3-year compound annual EPS growth using Bloomberg continuing operations EPS. Backward-looking track record of earnings compounding on a clean basis.' },
    fcfGrowth3yr: { formula: '(Latest FCF / FCF 3 Years Ago)^(1/3) - 1', description: 'Historical 3-year free cash flow CAGR. Cash generation growth — harder to manipulate than earnings growth.' },
    grossMarginExpansion3yr: { formula: 'Current Gross Margin - Gross Margin 3 Years Ago', description: 'Change in pricing power over 3 years. Expanding margins signal improving competitive position.' },
    opMarginExpansion3yr: { formula: 'Current Op Margin - Op Margin 3 Years Ago', description: 'Change in operating efficiency over 3 years. Expanding op margins indicate operational leverage or cost discipline.' },
    revenueGrowth3yr: { formula: '(Latest Revenue / Revenue 3 Years Ago)^(1/3) - 1', description: 'Historical 3-year revenue CAGR. Used for financials where FCF growth is less meaningful.' },
    // Safety
    altmanZ: { formula: '1.2×WC/TA + 1.4×RE/TA + 3.3×EBIT/TA + 0.6×MktCap/TL + Sales/TA', description: 'Bankruptcy prediction score. Above 3 = safe, 1.8-3 = gray zone, below 1.8 = distress risk. Combines 5 financial ratios.' },
    piotroski: { formula: '9-point binary scoring system', description: 'Fundamental strength score (0-9). Awards points for positive ROA, operating cash flow, margin improvement, leverage reduction, and liquidity improvement.' },
    currentRatio: { formula: 'Current Assets / Current Liabilities', description: 'Short-term liquidity — can the company cover obligations due within a year. Above 1 = adequate, above 2 = strong.' },
    interestCoverage: { formula: 'EBIT / Interest Expense', description: 'How many times over the company can pay its interest obligations from operating earnings. Higher = safer debt profile.' },
    fcfToDebt: { formula: 'Free Cash Flow / Total Debt', description: 'How quickly the company could pay off all debt from free cash flow. Higher = less leverage risk.' },
    debtToEbitda: { formula: 'Total Debt / EBITDA', description: 'Years of earnings needed to repay all debt. Lower = safer. Above 4x is typically considered high leverage.' },
    epsVariability: { formula: 'Std Dev of 5-year EPS / Mean EPS', description: 'Earnings stability — how consistent is the profit stream. Lower variability = more predictable business = safer.' },
    beta: { formula: 'Covariance(stock, market) / Variance(market)', description: 'Stock price sensitivity to market moves. Beta > 1 = more volatile than market. Lower beta = safer (inverted in scoring).' },
    equityToAssets: { formula: 'Total Equity / Total Assets', description: 'Capital ratio — what portion of assets are funded by equity vs debt. Higher = less leveraged, more resilient balance sheet.' },
    retainedEarningsToAssets: { formula: 'Retained Earnings / Total Assets', description: 'Cumulative profitability scaled by asset base. Higher = company has self-funded growth through earnings over time.' },
    // Payout
    netDebtPaydownYield: { formula: '(Prior Year Net Debt - Current Net Debt) / Market Cap', description: 'Rate of deleveraging as a percentage of market cap. Positive = company is paying down debt (good). Negative = adding debt (bad). Deleveraging makes equity more valuable.' },
    dividendYield: { formula: 'Annual Dividends Per Share / Price', description: 'Cash income return from dividends. A component of total shareholder yield.' },
    buybackYield: { formula: '(Bloomberg Current Shares - Prior Year Shares) / Prior Year Shares', description: 'Net buyback yield using Bloomberg current shares outstanding vs FMP prior year — captures true net share reduction after SBC dilution. FMP gross buyback figures significantly understate real capital return (e.g. GOOGL was +1.7% gross vs +53% net).' },
    // Momentum
    momentum: { formula: '12-Month Return minus Last Month Return', description: 'Classic 12-1 momentum factor. Stocks that have performed well over the past year (excluding the most recent month) tend to continue performing well.' },
    nearHigh: { formula: 'Current Price / 52-Week High', description: 'Proximity to the 52-week high. Stocks trading near their highs tend to have stronger forward returns (George & Hwang 2004). Higher = stronger momentum.' },
    shortTermReversal: { formula: '(Current Price / Price 1 Month Ago) - 1', description: 'Last month\'s return, inverted in scoring. Stocks that dropped sharply in the past month tend to bounce back (short-term reversal effect). Recent losers get a boost.' },
    insiderNetBuy: { formula: '(Insider Buy $ - Insider Sell $) / Total Insider Transaction $', description: 'SEC Form 4 insider trading signal over the past 6 months. Ranges from -1 (all selling) to +1 (all buying). Insider purchases are rare and highly predictive — executives buy their own stock when they see undervaluation the market hasn\'t priced.' },
    earningsSurprise: { formula: '(Actual EPS - Consensus EPS) / |Consensus EPS|', description: 'How much the company beat or missed analyst consensus at last earnings. Positive = beat, negative = miss. Post-earnings announcement drift is one of the most robust anomalies — stocks that beat tend to continue outperforming for weeks.' },
    // Valuation
    fwdPE: { formula: 'Price / Next Year Estimated EPS', description: 'Forward price-to-earnings using analyst consensus. Lower = cheaper relative to expected earnings.' },
    fwdPE2yr: { formula: 'Price / Year 2 Estimated EPS', description: 'Price relative to earnings 2 years out. Captures valuation vs medium-term earnings power.' },
    fwdPE3yr: { formula: 'Price / Year 3 Estimated EPS (BBG BEST_EPS_3BF)', description: 'Price relative to earnings 3 years out — Bloomberg consensus 3rd-year fiscal estimate. Captures whether a rich current multiple is justified by sustained compounding (NVDA, TSM get cheap here) vs speculative growth that doesn\'t catch up (PLTR stays expensive).' },
    fwdEvToRevenue: { formula: 'Enterprise Value / Next Year Estimated Revenue', description: 'Forward EV/Revenue — useful for high-growth companies where earnings may be depressed. Lower = cheaper.' },
    fwdEvToEbitda: { formula: 'Current Enterprise Value / BEST_EBITDA_1y', description: 'Forward TEV/EBITDA using Bloomberg consensus next-year EBITDA. The industry-standard forward multiple — capital-structure-neutral and not dependent on book value. Lower = cheaper.' },
    pegRatio: { formula: 'Forward P/E / EPS Growth Rate', description: 'Price-to-earnings adjusted for growth. A PEG of 1 means fairly valued for its growth rate. Lower = cheaper for the growth you get.' },
    evToEbitda: { formula: 'Enterprise Value / EBITDA', description: 'Enterprise value relative to cash earnings before interest, taxes, depreciation. The most widely used valuation multiple. Lower = cheaper.' },
    pToFcf: { formula: 'Market Cap / Free Cash Flow', description: 'Price relative to actual cash generation. More conservative than P/E since FCF is harder to manipulate.' },
    evToGrossProfit: { formula: 'Enterprise Value / Gross Profit', description: 'Buffett-style valuation — prices the business relative to its gross economic output. Lower = cheaper.' },
    pToRevenue: { formula: 'Market Cap / Revenue', description: 'Price-to-sales — useful for unprofitable or cyclical companies. Lower = cheaper relative to top-line.' },
    priceToBook: { formula: 'Market Cap / Total Stockholders\' Equity', description: 'Trailing price-to-book. How the market values each dollar of book equity. Below 1x suggests the market thinks book value is overstated; high P/B implies intangible goodwill or growth expectations.' },
    pToFfo: { formula: 'Price / Funds From Operations', description: 'REIT-canonical multiple. FFO adds depreciation back to net income because real-estate D&A is non-cash and overstates the GAAP earnings decline. Lower = cheaper for the cash a REIT actually generates. Applies only to REITs (nulled on non-REIT peer groups).' },
    pToNav: { formula: 'Price / Net Asset Value', description: 'Price relative to the appraised market value of property holdings minus liabilities. Trading below NAV means the stock implies the property portfolio is worth less than third-party appraisers say. Applies only to REITs.' },
    fwdPtoBook: { formula: 'Price / BEST_BPS_1y', description: 'Forward price-to-book using Bloomberg consensus forward book value per share (next fiscal year). Captures expected equity growth — a company compounding book value fast looks cheaper on a forward basis than trailing P/B suggests. Direct from BBG, no derivation.' },
    fwdPtoSales: { formula: 'Price / (BEST_SALES_1y / Implied_Forward_Shares)', description: 'Forward price-to-sales adjusted for expected buybacks. Implied forward shares = BEST_NET_INCOME_1y / BEST_EPS_1y — derives the share count analysts bake into their EPS forecasts. For companies that buy back stock (GOOGL, AAPL, MSFT), this captures the per-share revenue uplift that a trailing P/S misses. Lower = cheaper.' },
    nextQtrEpsGrowth: { formula: '(BEST_EPS_1FQ - Prior_Year_Same_Q_Actual) / |Prior_Year_Same_Q_Actual|', description: 'Forward YoY quarterly EPS growth — next fiscal quarter consensus vs the actual reported EPS from the same fiscal quarter one year ago. More responsive than fiscal-year growth because it captures current-cycle guidance shifts + estimate revisions within the fiscal year. Prior-year same-Q actual pulled from Bloomberg\'s EARN_ANN_DT_TIME_HIST_WITH_EPS bulk field.' },
    fwdRoe: { formula: 'BEST_ROE at 1BF', description: 'Bloomberg consensus forward return on equity (next fiscal year). Measures expected profitability of shareholder capital — forward-looking Profitability factor. The market prices companies on what they\'re expected to earn, not what they earned last year.' },
    fwdRoa: { formula: 'BEST_ROA at 1BF', description: 'Bloomberg consensus forward return on assets (next fiscal year). Expected profitability per dollar of assets. Excluded for banks where analyst ROA forecasts are historically unreliable.' },
    fwdDividendYield: { formula: 'BEST_DPS_1y / Price', description: 'Forward dividend yield = consensus forward dividend per share divided by current price. Captures expected dividend changes (hikes / cuts / initiations) that trailing yield misses.' },
    impliedBuybackYield: { formula: '(Current_Shares - Implied_Forward_Shares) / Current_Shares', description: 'Expected share count reduction over next fiscal year, derived from the analyst-implied forward share count (BEST_NET_INCOME_1y / BEST_EPS_1y). Positive = buyback expected, negative = dilution expected. A forward-looking complement to trailing buyback yield.' },
  };
  return info[key] ?? null;
}
